import {
  UploadFailedFilesModal,
  UploadProgressModal
} from '@business/workflows/ComponentUI/Dashboard';
import { FilesUploadModal } from '@business/workflows/ComponentUI/UploadFilesModal';
import { getFolderDetails } from '@ports/common/folderStateSlice';
import {
  getAdditionalPhotoMeta,
  getIsFromFavoritePage,
  getIsMediaUploadModalOpen,
  getModuleForUpload,
  getSharedSubscriberId,
  getSubscriberId,
  getToken,
  getUploadFilesSize,
  getUserNotificationData,
  setAdditionalPhotoMeta,
  setIsFromFavoritePage,
  setIsMediaUploadModalOpen,
  setUserNotificationData,
  setUploadFilesSize,
  incrementAssetRefreshCounter
} from '@ports/common/sessionStateSlice';
import { getUserData } from '@ports/common/userStateSlice';
import { useAddGroupAssetsMutation } from '@ports/internal-services/group/groupSlice';
import { getUploadFolder } from '@utils/helpers/lib/mediaType';
import {
  uploadMultiplePhotos,
  validateMultipleFiles
} from '@utils/helpers/s3Upload';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import socketService from '@business/services/SocketService';
import { showToast } from '@business/workflows/ComponentUI/Toast/Toast';
import { useLocation } from 'react-router-dom';
import { UploadProgressToast } from './UploadProgressToast';
import { GedcomImportToast } from './GedcomImportToast';

const GROUP_TOAST_ID = 'photo-upload-progress';

// Maps the internal moduleForUpload value to the module name expected by the addGroupAssets API
const UPLOAD_MODULE_TO_API_MODULE: Record<string, string> = {
  peopleDetail: 'people',
  moments: 'moments',
  group: 'group'
};

// Maps the API module name to the corresponding id field stored in additionalPhotoMeta
const API_MODULE_TO_META_ID_KEY: Record<string, string> = {
  people: 'peopleId',
  moments: 'momentId',
  group: 'groupId'
};

export const MediaUploadPersister = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const token = useSelector(getToken);
  const sharedSubscriberIdFromSlice = useSelector(getSharedSubscriberId);
  const queryParams = new URLSearchParams(window.location.search);
  const source = queryParams.get('source'); // 'myFiles' | 'sharedWithMe' | null
  const isFromSharedWithMe = source === 'sharedWithMe';
  const subscriberId = useSelector(getSubscriberId);
  const userDetails = useSelector(getUserData);
  const isOpenFilesModal = useSelector(getIsMediaUploadModalOpen);
  const userNotificationData = useSelector(getUserNotificationData);
  const folderDetails = useSelector(getFolderDetails);
  const isFromFavoritePage = useSelector(getIsFromFavoritePage);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [failedFiles, setFailedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0
  });
  const uploadAbortRef = useRef<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const additionalMeta = useSelector(getAdditionalPhotoMeta);
  const moduleForUpload = useSelector(getModuleForUpload);
  const uploadFilesSize = useSelector(getUploadFilesSize);
  const [addGroupAssets] = useAddGroupAssetsMutation();

  const isGallerySelectEnabled = Boolean(
    location?.pathname?.includes('/sharing')
  );

  useEffect(() => {
    dispatch(
      setUserNotificationData({
        page: '',
        notificationData: null
      })
    );
    // refetchStorage();
  }, []);

  useEffect(() => {
    if (userNotificationData?.page === 'gedcom_import') {
      const current = userNotificationData?.notificationData?.current
        ? Number(userNotificationData?.notificationData?.current || 0)
        : null;
      const status =
        userNotificationData?.notificationData?.status || 'inprogress';
      toast.success(<GedcomImportToast status={status} current={current} />, {
        closeOnClick: true,
        hideProgressBar: true,
        icon: false,
        style: {
          padding: 0, // remove Toastify default padding
          background: 'transparent'
        }
      });
      dispatch(
        setUserNotificationData({
          page: '',
          notificationData: null
        })
      );
      return;
    }

    if (
      userNotificationData?.page === 'photos' ||
      userNotificationData?.page === 'recipe'
    ) {
      const current = Number(
        userNotificationData?.notificationData?.uploadIndex
      );
      const total = Number(userNotificationData?.notificationData?.uploadTotal);
      const iconUrl = userNotificationData?.notificationData?.thumbnailUrl;

      if (total === 0) return;

      setUploadProgress({
        current,
        total
      });

      if (current >= total) {
        dispatch(
          setUserNotificationData({
            page: '',
            notificationData: null
          })
        );
      }

      if (!toast.isActive(GROUP_TOAST_ID)) {
        toast.success(
          <UploadProgressToast
            current={current}
            total={total}
            iconUrl={iconUrl}
          />,
          {
            toastId: GROUP_TOAST_ID,
            closeOnClick: true,
            hideProgressBar: true,
            icon: false,
            style: {
              padding: 0, // remove Toastify default padding
              background: 'transparent'
            }
          }
        );
      } else {
        toast.update(GROUP_TOAST_ID, {
          render: (
            <UploadProgressToast
              current={current}
              total={total}
              iconUrl={iconUrl}
            />
          ),
          type: 'success'
        });
      }
    }
  }, [userNotificationData]);

  const photoUploadHandler = async (files: File[]) => {
    if (showUploadModal && files?.length === 1) {
      setShowUploadModal(false);
    }

    const validFiles: File[] = [];
    const invalidFiles: File[] = [];

    files.forEach(file => {
      const validation = validateMultipleFiles([file], 1);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    });

    if (invalidFiles?.length > 0) {
      const failedFileNames = invalidFiles.map(f => f.name).join(' and ');
      const errorMsg = t('dashboard.unsupportedFormatMessage', {
        fileNames: failedFileNames
      });

      setUploadError(errorMsg);
      setFailedFiles(invalidFiles);

      toast.error(
        <div>
          <div className="font-medium mb-1">
            {t('dashboard.unsupportedFormat')}
          </div>
          <div className="text-xs">
            {t('dashboard.unsupportedFormatMessage', {
              fileNames: failedFileNames
            })}
          </div>
        </div>,
        {
          autoClose: false,
          onClick: () => setShowUploadModal(true)
        }
      );
      return;
    }

    if (validFiles?.length === 0) return;

    const totalFiles = validFiles?.length + invalidFiles?.length;
    setUploadProgress({ current: 0, total: totalFiles });
    setIsUploading(true);

    const uploadFailedFiles: File[] = [];
    let successCount = 0;

    try {
      const idToken = token || '';

      if (!idToken) {
        throw new Error(t('dashboard.authenticationRequired'));
      }
      socketService.connect();

      const metaData = {
        ...additionalMeta
      };

      if (isFromSharedWithMe) {
        metaData.subscriberId = sharedSubscriberIdFromSlice;
        metaData.status = 'Pending';
        metaData.createdBy = userDetails?.userId || '';
        const match = window.location.pathname.match(/\/people\/([a-f0-9-]+)/i);
        metaData.peopleId = match ? match[1] : '';
      }

      for (let i = 0; i < validFiles?.length; i++) {
        if (uploadAbortRef.current) break;
        let folder = getUploadFolder(validFiles[i]);
        try {
          const uploadMetadata: any = {
            createdBy: userDetails?.userId || '',
            userId: userDetails?.userId || '',
            folderId: folderDetails?.folderId || '',
            uploadIndex: (i + 1).toString(),
            uploadTotal: totalFiles.toString(),
            ...metaData
          };
          // Only add isFavorite if isFromFavoritePage is true
          if (isFromFavoritePage) {
            uploadMetadata.isFavorite = true;
          }

          // Use contributor's subscriberId for S3 path when uploading on their behalf
          const uploadSubscriberId =
            (additionalMeta as any)?.subscriberId || subscriberId;

          await uploadMultiplePhotos(
            [validFiles[i]],
            idToken,
            userDetails?.userId || '',
            folder,
            () => {
              setUploadProgress({
                current: successCount + 1,
                total: totalFiles
              });
            },
            uploadSubscriberId,
            uploadMetadata
          );

          successCount++;
        } catch (fileError: any) {
          console.error(`Failed to upload ${validFiles[i].name}:`, fileError);
          uploadFailedFiles.push(validFiles[i]);
        }
      }

      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0 });

      // Reset isFromFavoritePage after successful uploads
      if (isFromFavoritePage) {
        dispatch(setIsFromFavoritePage(false));
      }

      // Reset additionalPhotoMeta after upload (e.g. status: 'pending' from sharing module)
      if (Object.keys(additionalMeta || {})?.length > 0) {
        dispatch(setAdditionalPhotoMeta({}));
      }

      if (invalidFiles?.length > 0) {
        const failedFileNames = invalidFiles.map(f => f.name).join(' and ');
        const errorMsg = t('dashboard.unsupportedFormatMessage', {
          fileNames: failedFileNames
        });

        setUploadError(errorMsg);
        setFailedFiles(invalidFiles);

        toast.error(
          <div>
            <div className="font-semibold mb-1">
              {t('dashboard.uploadProgressPhotos', {
                current: successCount,
                total: totalFiles
              })}
            </div>
            <div className="font-medium mb-1">
              {t('dashboard.unsupportedFormat')}
            </div>
            <div className="text-xs">
              {t('dashboard.unsupportedFormatMessage', {
                fileNames: failedFileNames
              })}
            </div>
          </div>,
          {
            autoClose: false,
            onClick: () => setShowUploadModal(true)
          }
        );
      } else if (uploadFailedFiles?.length > 0) {
        const failedFileNames = uploadFailedFiles
          .map(f => f.name)
          .join(' and ');
        const errorMsg = t('dashboard.uploadFailedMessage', {
          fileNames: failedFileNames
        });

        setUploadError(errorMsg);
        setFailedFiles(uploadFailedFiles);

        toast.error(
          <div>
            <div className="font-semibold mb-1">
              {t('dashboard.uploadProgressPhotos', {
                current: successCount,
                total: totalFiles
              })}
            </div>
            <div className="font-medium mb-1">
              {t('dashboard.uploadFailed')}
            </div>
            <div className="text-xs">
              {t('dashboard.uploadFailedMessage', {
                fileNames: failedFileNames
              })}
            </div>
          </div>,
          {
            autoClose: false,
            onClick: () => setShowUploadModal(true)
          }
        );
      }
    } catch (error: any) {
      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0 });

      // Reset isFromFavoritePage even on error
      if (isFromFavoritePage) {
        dispatch(setIsFromFavoritePage(false));
      }

      // Reset additionalPhotoMeta even on error
      if (Object.keys(additionalMeta || {})?.length > 0) {
        dispatch(setAdditionalPhotoMeta({}));
      }

      const totalFiles =
        (validFiles?.length || 0) + (invalidFiles?.length || 0);
      const failedFileNames = validFiles
        .slice(successCount)
        .map(f => f.name)
        .join(' and ');
      const errorMsg = error.message || t('dashboard.failedToUpload');

      setUploadError(errorMsg);
      setFailedFiles([...validFiles.filter((_, i) => i >= successCount)]);

      toast.error(
        <div>
          <div className="font-semibold mb-1">
            {t('dashboard.uploadProgressPhotos', {
              current: successCount,
              total: totalFiles
            })}
          </div>
          <div className="font-medium mb-1">{t('dashboard.uploadFailed')}</div>
          <div className="text-xs">
            {failedFileNames
              ? t('dashboard.uploadFailedMessage', {
                  fileNames: failedFileNames
                })
              : ''}
            {error.message || t('dashboard.failedToUpload')}
          </div>
        </div>,
        {
          autoClose: false,
          onClick: () => setShowUploadModal(true)
        }
      );
    } finally {
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
      uploadAbortRef.current = false;
    }
  };

  const gallerySelectHandler = async (assetIds: string[]) => {
    let module = UPLOAD_MODULE_TO_API_MODULE[moduleForUpload || ''] || '';
    if (!module && isGallerySelectEnabled) {
      module = 'photos';
    }

    const idKey = API_MODULE_TO_META_ID_KEY[module];
    const ids = idKey ? (additionalMeta as any)?.[idKey] : '';

    if (!module || (!ids && module !== 'photos') || assetIds.length === 0)
      return;

    try {
      await addGroupAssets({
        groupAssets: {
          module,
          ids,
          size: uploadFilesSize || '0',
          assetIds,
          subscriberId: (additionalMeta as any)?.subscriberId || subscriberId
        }
      }).unwrap();

      dispatch(incrementAssetRefreshCounter());
      dispatch(setUploadFilesSize(null));
      showToast({
        type: 'success',
        isAlert: true,
        alertProps: {
          type: 'success',
          message: t('assetAddSuccess')
        }
      });
    } catch {
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: t('assetAddFail')
        }
      });
    }
  };

  const onSocketMessageReceived = useCallback((data: any) => {
    if (document.visibilityState === 'visible') {
      const payload = data?.payload;
      // User-facing share/contribution notifications are handled by the
      // notification bell (useNotificationSocket); ignore them here so they
      // don't trigger an unrelated asset refetch.
      if (payload?.kind === 'userNotification') return;
      dispatch(
        setUserNotificationData({
          page: payload?.mediaSource ?? 'photos',
          notificationData: payload
        })
      );
    }
  }, []);

  useEffect(() => {
    socketService.subscribe('onMessage', 'app.event', onSocketMessageReceived);
    return () => {
      socketService.unsubscribe('onMessage', 'app.event');
    };
  }, [onSocketMessageReceived]);

  return (
    <>
      <FilesUploadModal
        isOpen={isOpenFilesModal}
        onClose={() => dispatch(setIsMediaUploadModalOpen(false))}
        onUpload={files => {
          dispatch(setIsMediaUploadModalOpen(false));
          photoUploadHandler(files);
        }}
        onGallerySelect={assetIds => {
          dispatch(setIsMediaUploadModalOpen(false));
          gallerySelectHandler(assetIds);
        }}
        isGallerySelectEnabled={isGallerySelectEnabled}
      />

      <input
        title="Upload photos and videos"
        type="file"
        accept="image/*,video/*"
        multiple
        ref={fileInputRef}
        id="common-photo-upload-input"
        className="hidden"
        onChange={e => {
          const files = Array.from(e.target.files || []);
          if (files?.length > 0) {
            photoUploadHandler(files);
          }
        }}
      />

      {showUploadModal && (
        <UploadFailedFilesModal
          isOpen={showUploadModal}
          onClose={() => {
            setShowUploadModal(false);
            setUploadError('');
            setFailedFiles([]);
          }}
          onUpload={photoUploadHandler}
          errorMessage={uploadError}
          failedFiles={failedFiles}
          setShowUploadModal={setShowUploadModal}
        />
      )}

      {isUploading && (
        <UploadProgressModal
          isOpen={isUploading}
          current={uploadProgress.current}
          total={uploadProgress.total}
          onClose={() => setIsUploading(false)}
        />
      )}
    </>
  );
};
